import React from 'react'
import { Box, FormControl, MenuItem, Select, Typography } from '@mui/material'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { ActivityType } from '../../types'
import {
  getLoopOptions,
  getResolvedLoopCount,
  getSlotLengthMinutes,
  shouldShowLoopConfiguration
} from '../../utils/activityConfiguration'
import { getActivityTemplateByType } from '../../constants/activities'
import { TESTIDS } from '../../../../../config/testids'

interface GameActivityLoopSettingsProps {
  activityType: ActivityType
  numberOfLoops?: number
  onNumberOfLoopsChange?: (numberOfLoops: number) => void
  variant?: 'compact' | 'panel'
  errorText?: string
}

/**
 * Render loop controls and runtime summary for game activities.
 *
 * @param {GameActivityLoopSettingsProps} props - Component props.
 * @return {JSX.Element | null} Loop configuration UI or null for non-game activities.
 */
export const GameActivityLoopSettings: React.FC<GameActivityLoopSettingsProps> = ({
  activityType,
  numberOfLoops,
  onNumberOfLoopsChange,
  variant = 'compact',
  errorText
}) => {
  const { t } = useTranslation()

  if (!shouldShowLoopConfiguration(activityType)) {
    return null
  }

  const loopOptions = getLoopOptions(activityType)
  const resolvedLoopCount = getResolvedLoopCount(activityType, numberOfLoops) ?? loopOptions[0]
  const template = getActivityTemplateByType(activityType)
  // Slot length shown here is slot + buffer for planning; game receives slot only.
  const slotLengthMinutes = getSlotLengthMinutes(template?.customSlotLength, template?.slotDisplayBufferMinutes)
  const isCompact = variant === 'compact'

  return (
    <Box
      sx={{
        mt: isCompact ? 1.5 : 0,
        p: isCompact ? 1.5 : 2,
        borderRadius: '12px',
        border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
        backgroundColor: INTERAX_COLORS.heritageBlue
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: isCompact ? 'row' : 'column' },
          gap: 1.5,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: isCompact ? 'center' : 'stretch' }
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: INTERAX_COLORS.white }}>
            {t('facilitator.activityConfig.loopCountLabel')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: INTERAX_COLORS.thunderGrey }}>
            {t('facilitator.activityConfig.loopCountHint', { minutes: slotLengthMinutes })}
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: isCompact ? 92 : 120 }}>
          <Select
            value={resolvedLoopCount}
            onChange={(event) => onNumberOfLoopsChange?.(Number(event.target.value))}
            data-testid={TESTIDS.SPRINT_GAME_LOOPS_SELECT}
            sx={{
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              color: INTERAX_COLORS.white,
              borderRadius: '8px',
              '& .MuiSvgIcon-root': {
                color: INTERAX_COLORS.white
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: INTERAX_COLORS.thunderGrey
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: INTERAX_COLORS.heritageBlue,
                  color: INTERAX_COLORS.white,
                  border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
                  '& .MuiMenuItem-root': {
                    color: INTERAX_COLORS.white,
                    '&:hover': {
                      backgroundColor: INTERAX_COLORS.heritageBlue40
                    },
                    '&.Mui-selected': {
                      backgroundColor: INTERAX_COLORS.sun,
                      color: INTERAX_COLORS.offBlack
                    }
                  }
                }
              }
            }}
          >
            {loopOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: '999px',
            backgroundColor: INTERAX_COLORS.sun,
            color: INTERAX_COLORS.offBlack,
            fontSize: '12px',
            fontWeight: 600
          }}
        >
          {t('facilitator.activityConfig.loopCountSummary', { count: resolvedLoopCount })}
        </Box>
        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: '999px',
            backgroundColor: INTERAX_COLORS.heritageBlue40,
            color: INTERAX_COLORS.white,
            fontSize: '12px',
            fontWeight: 600
          }}
        >
          {t('facilitator.activityConfig.slotLengthSummary', { minutes: slotLengthMinutes })}
        </Box>
      </Box>

      {errorText && (
        <Typography sx={{ mt: 1, fontSize: '12px', color: INTERAX_COLORS.redError }}>{errorText}</Typography>
      )}
    </Box>
  )
}
